import React from 'react';
import { Textarea } from '../ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Button } from '../ui/button';
import { Send, AtSign, Square, Zap, Lock } from 'lucide-react';
import { toast } from 'sonner';

const ChatInput = ({
  input, setInput, loading, disabled, subagents, textareaRef,
  showMentionDropdown, setShowMentionDropdown, mentionFilter, setMentionFilter,
  mentionIndex, setMentionIndex, onSend, onStop,
  selectedAgent, enableUltra, setEnableUltra, messages, user,
}) => {
  const handleChange = (e) => {
    const val = e.target.value;
    setInput(val);
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.substring(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@([\w\u4e00-\u9fff\u3400-\u4dbf]*)$/);
    if (atMatch && subagents.length > 0) {
      setShowMentionDropdown(true);
      setMentionFilter(atMatch[1]);
      setMentionIndex(0);
    } else {
      setShowMentionDropdown(false);
      setMentionFilter('');
    }
  };

  const insertMention = (sa) => {
    const atIdx = input.lastIndexOf('@');
    setInput(input.substring(0, atIdx) + `@${sa.name} `);
    setShowMentionDropdown(false);
    setMentionFilter('');
    setMentionIndex(0);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (showMentionDropdown) {
      const filtered = subagents.filter(
        (sa) => !mentionFilter || sa.name.toLowerCase().includes(mentionFilter.toLowerCase())
      );
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex((prev) => (prev + 1) % Math.max(filtered.length, 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setMentionIndex((prev) => (prev - 1 + filtered.length) % Math.max(filtered.length, 1)); return; }
      if (e.key === 'Enter' && filtered.length > 0) { e.preventDefault(); if (filtered[mentionIndex]) insertMention(filtered[mentionIndex]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setShowMentionDropdown(false); return; }
    }
    const isMobile = window.innerWidth < 768;
    if (e.key === 'Enter' && !e.shiftKey && !isMobile) { e.preventDefault(); setShowMentionDropdown(false); onSend(); }
  };

  const filtered = subagents.filter(
    (sa) => !mentionFilter || sa.name.toLowerCase().includes(mentionFilter.toLowerCase())
  );

  const hasUltraConfig = selectedAgent?.review_config?.review_enabled === true;
  const hasUserMessages = messages.some((msg) => msg.role === 'user');
  const ultraDisabled = hasUserMessages || !hasUltraConfig;

  const triggerUltraBurst = (e) => {
    if (!hasUltraConfig) { toast.info('此 Agent 未配置 Ultra 功能', { duration: 3000 }); return; }
    if (user?.role === 'user') { toast.info('Ultra 模式僅限 VIP 用戶使用', { duration: 4000 }); return; }
    if (hasUserMessages) return;

    if (!enableUltra) {
      const rect = e.currentTarget.getBoundingClientRect();
      const burst = document.createElement('div');
      burst.className = 'ultra-burst-overlay';
      burst.style.setProperty('--burst-x', `${rect.left + rect.width / 2}px`);
      burst.style.setProperty('--burst-y', `${rect.top + rect.height / 2}px`);
      document.body.appendChild(burst);
      setTimeout(() => burst.remove(), 900);
    }
    setEnableUltra(!enableUltra);
  };

  return (
    <div className="bg-atelier-bg border-t border-atelier-line p-4 md:p-5 flex-shrink-0 mb-14 md:mb-0 relative z-10">
      <div className="max-w-[900px] mx-auto">
        {subagents.length > 0 && (
          <div
            className="flex items-center gap-1.5 mb-3 overflow-x-auto scrollbar-hide"
            data-testid="subagent-chips"
          >
            <AtSign className="w-3.5 h-3.5 text-atelier-subtle flex-shrink-0" strokeWidth={1.6} />
            <span className="atelier-caption flex-shrink-0 mr-1">Mention</span>
            {subagents.map((sa) => (
              <TooltipProvider key={sa.id} delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => {
                        if (!input.startsWith(`@${sa.name} `)) setInput(`@${sa.name} ` + input);
                        textareaRef.current?.focus();
                      }}
                      className="flex items-center gap-1.5 px-2.5 h-7 rounded-full border text-[11px] font-medium transition-all duration-300 flex-shrink-0"
                      style={{
                        borderColor: sa.color + '55',
                        color: sa.color,
                        backgroundColor: sa.color + '12',
                      }}
                      data-testid={`subagent-chip-${sa.id}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: sa.color }} />
                      {sa.name}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="text-xs max-w-xs p-2">{sa.description}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        )}

        {/* ── Input card — paper with 1px hairline, coral focus border ── */}
        <div
          className="bg-atelier-surface border border-atelier-line rounded-[22px] focus-within:border-atelier-coral/60 transition-colors duration-300 relative"
        >

          {showMentionDropdown && filtered.length > 0 && (
            <div
              className="absolute bottom-full left-0 right-0 mb-1.5 bg-atelier-surface border border-atelier-line rounded-xl z-50 max-h-48 overflow-y-auto"
              data-testid="mention-dropdown"
            >
              {filtered.map((sa, i) => (
                <button
                  key={sa.id}
                  className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors border-b border-atelier-line last:border-b-0 ${
                    i === mentionIndex ? 'bg-atelier-surface2' : 'hover:bg-atelier-surface2/60'
                  }`}
                  onClick={() => insertMention(sa)}
                  data-testid={`mention-option-${sa.id}`}
                >
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: sa.color }} />
                  <div>
                    <div className="text-sm font-medium text-atelier-ink">{sa.name}</div>
                    <div className="text-xs text-atelier-mute">{sa.description}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="atelier-ink-open relative">
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none px-5 pt-4 md:px-6 md:pt-5 whitespace-pre-wrap break-words overflow-hidden text-transparent"
              style={{ fontSize: '16px', lineHeight: '1.6' }}
            >
              {input.replace(/^(@[\w\u4e00-\u9fff\u3400-\u4dbf]+)/, '\u200B$1\u200B').split(/\u200B/).map((part, i) => {
                if (/^@[\w\u4e00-\u9fff\u3400-\u4dbf]+$/.test(part)) {
                  const sa = subagents.find((s) => `@${s.name}` === part);
                  return (
                    <span
                      key={i}
                      className="rounded px-0.5"
                      style={{ backgroundColor: (sa?.color || '#FF5436') + '40', color: 'transparent' }}
                    >
                      {part}
                    </span>
                  );
                }
                return <span key={i}>{part}</span>;
              })}
            </div>
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onBlur={() => setTimeout(() => setShowMentionDropdown(false), 200)}
              placeholder={subagents.length > 0 ? '輸入訊息… 輸入 @ 呼叫助手' : '輸入訊息…'}
              className="resize-none bg-transparent border-none text-atelier-ink placeholder:text-atelier-subtle focus:outline-none focus:ring-0 w-full px-5 pt-4 pb-2 md:px-6 md:pt-5 md:pb-3"
              rows={1}
              disabled={loading || disabled}
              data-testid="message-input"
              style={{ minHeight: '64px', fontSize: '16px', lineHeight: '1.6' }}
            />
          </div>

          <div className="flex items-center justify-between px-4 md:px-5 pb-3 pt-1 border-t border-atelier-line">
            <div className="flex items-center gap-2">
              {selectedAgent && (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={triggerUltraBurst}
                        disabled={ultraDisabled}
                        className={`flex items-center gap-1.5 px-3 h-7 rounded-full border text-[11px] font-medium transition-all duration-300 ${
                          ultraDisabled ? 'opacity-40 cursor-not-allowed' : ''
                        } ${
                          enableUltra && hasUltraConfig
                            ? 'bg-atelier-coral/10 border-atelier-coral/50 text-atelier-coral'
                            : 'bg-transparent border-atelier-line text-atelier-mute hover:border-atelier-lineHi hover:text-atelier-ink'
                        }`}
                        data-testid="ultra-mode-btn"
                      >
                        <Zap
                          className="w-3 h-3"
                          strokeWidth={2}
                          fill={enableUltra && hasUltraConfig ? 'currentColor' : 'none'}
                        />
                        <span>Ultra</span>
                        {user?.role === 'user' && <Lock className="w-2.5 h-2.5" strokeWidth={2} />}
                        {hasUserMessages && <Lock className="w-2.5 h-2.5" strokeWidth={2} />}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="text-xs max-w-xs p-2">
                      {!hasUltraConfig
                        ? '此 Agent 未配置 Ultra 功能'
                        : user?.role === 'user'
                        ? 'Ultra 模式僅限 VIP 用戶使用'
                        : hasUserMessages
                        ? '對話開始後無法更改，請新建對話'
                        : '啟用 Ultra 模式會使用 AI 品質審核，消耗 2-3倍 credits'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {loading || disabled ? (
              <Button
                onClick={onStop}
                className="rounded-full w-10 h-10 p-0 bg-red-500 hover:bg-red-600 text-white transition-all duration-300"
                data-testid="stop-btn"
                title="停止生成"
              >
                <Square className="w-3.5 h-3.5" strokeWidth={3} fill="currentColor" />
              </Button>
            ) : (
              <button
                onClick={onSend}
                disabled={!input.trim()}
                className={`rounded-full w-10 h-10 p-0 flex items-center justify-center transition-all duration-300 ${
                  input.trim()
                    ? 'bg-atelier-coral text-white hover:bg-atelier-coralHi hover:scale-105 atelier-glow-coral'
                    : 'bg-transparent border border-atelier-line text-atelier-subtle cursor-not-allowed'
                }`}
                data-testid="send-btn"
              >
                <Send className="w-4 h-4" strokeWidth={1.8} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
